import { pasteTextFromClipboard, useIntl } from '@capsicum/common';
import { BaseTextInput, Button, ModalService, QRScanner, type QRScannerProps, Text } from '@capsicum/common-web';
import { PasteIcon } from '@capsicum/common-web/icons';
import React, { type ChangeEvent, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import styled from 'styled-components';

type ScannerModalProps = {
	clearModals: () => void;
	innerStyle?: QRScannerProps['innerStyle'];
	onScanSuccess: QRScannerProps['onScanSuccess'];
	options?: QRScannerProps['options'];
};

export const ScannerModal = ({ clearModals, innerStyle, onScanSuccess, options = {} }: ScannerModalProps) => {
	const { $t } = useIntl();
	const [walletConnectCode, handleTextChange] = useState('');
	const [isManualInputVisible, changeManualInputVisibility] = useState(false);
	const enableManualInput = () => changeManualInputVisibility(true);
	const isSubmitDisabled = !walletConnectCode;

	const onScan: QRScannerProps['onScanSuccess'] = result => {
		ModalService.clearModals();
		onScanSuccess(result);
	};

	const pasteCode = async () => {
		try {
			handleTextChange(await pasteTextFromClipboard());
		} catch (_e) {
			// Browsers sometimes don't allow pasting and throw instead
		}
	};

	const submitManualWalletConnectCode = () => {
		if (walletConnectCode) {
			onScanSuccess({ cornerPoints: [], data: walletConnectCode });
			ModalService.clearModals();
		}
	};

	return (
		<ClickAwayListener onClickAway={clearModals}>
			<S.Wrapper>
				<S.Title color="main.enabled" variant="text6">
					{$t('ncWallet.walletConnect.scannerModal.title')}
				</S.Title>
				<S.Scanner innerStyle={{ ...innerStyle }} options={options} onScanSuccess={onScan} />
				<S.ManualInputWrapper>
					{isManualInputVisible ? (
						<S.ManualFormWrapper>
							<S.ManualInput
								iconEnd={{
									icon: PasteIcon,
									onClick: pasteCode
								}}
								label={$t('ncWallet.walletConnect.scannerModal.inputTitle')}
								name="walletConnectCode"
								testID="qr-modal-manual-input"
								value={walletConnectCode}
								onChange={(event: ChangeEvent<HTMLInputElement>) =>
									handleTextChange(event.target.value)
								}
							/>
							<S.ManualFormActions>
								<Button
									testID="qr-modal-manual-button-cancel"
									text={$t('global.button.cancel')}
									variant="outlined"
									onClick={ModalService.clearModals}
								/>
								<Button
									disabled={isSubmitDisabled}
									testID="qr-modal-manual-button-confirm"
									text={$t('global.button.confirm')}
									onClick={submitManualWalletConnectCode}
								/>
							</S.ManualFormActions>
						</S.ManualFormWrapper>
					) : (
						<S.ManualInputButton
							testID="qr-modal-manual-input-button"
							text={$t('ncWallet.walletConnect.scannerModal.manualButtonText')}
							variant="outlined"
							onClick={enableManualInput}
						/>
					)}
				</S.ManualInputWrapper>
			</S.Wrapper>
		</ClickAwayListener>
	);
};

namespace S {
	export const ManualFormWrapper = styled.div`
		display: block;
		width: 100%;
	`;

	export const ManualFormActions = styled.div`
		display: flex;
		justify-content: center;
		width: 100%;
		align-items: center;
		gap: ${({ theme }) => theme.sizing.small};
		margin-top: ${({ theme }) => theme.sizing.small};
	`;

	export const ManualInputWrapper = styled.div`
		display: flex;
		margin-top: ${({ theme }) => theme.sizing.small};
	`;

	export const ManualInput = styled(BaseTextInput)`
		width: 100%;
	`;

	export const Wrapper = styled.div`
		display: block;
		margin: 0 auto;
		width: fit-content;
		${({ theme }) => theme.border.radius.medium};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		padding-bottom: ${({ theme }) => theme.sizing.xxsmall};
	`;

	export const Title = styled(Text)`
		text-align: center;
		padding-left: ${({ theme }) => theme.sizing.xxxsmall};
		padding-right: ${({ theme }) => theme.sizing.xxxsmall};
		padding-top: ${({ theme }) => theme.sizing.medium};
		padding-bottom: ${({ theme }) => theme.sizing.large};
		max-width: 50vh;

		${({ theme }) => theme.breakpoints.queries.only.mediumDesktop} {
			max-width: 40vh;
		}

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			width: 80vw;
			padding-top: ${({ theme }) => theme.sizing.small};
			padding-bottom: ${({ theme }) => theme.sizing.small};
		}
	`;

	export const ManualInputButton = styled(Button)`
		text-align: center;
		margin: 0 auto;
	`;

	export const Scanner = styled(QRScanner)`
		display: block;
		margin: 0 auto;
		height: 50vh;
		max-width: 50vh;

		${({ theme }) => theme.breakpoints.queries.only.mediumDesktop} {
			height: 40vh;
			max-width: 40vh;
		}

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			aspect-ratio: 1/1;
			width: 80vw;
			height: 80vw;
		}
	`;
}
